import type { ReactNode } from 'react'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownOutlined,
  PauseCircleOutlined,
  TruckOutlined,
} from '../../../components/icons'
import { Chip } from '../../../components/ui/Chip'
import type { ChipTone } from '../../../components/ui/Chip'
import { DropdownMenu } from '../../../components/ui/DropdownMenu'
import { BOM_STATUS_LABEL } from '../types'
import type { BomLibraryStatus } from '../types'

const BOM_STATUS_OPTIONS: readonly BomLibraryStatus[] = [
  'draft',
  'in_review',
  'approved',
  'ordered',
]

/**
 * UID-295/296 Phase 1 — the ONE canonical status→tone/icon map both BOM
 * screens render from (screens.md §5/§7; the measured design spec's chip
 * colors: draft=neutral, in review=amber/`warning`, approved=green/`success`,
 * ordered=blue/`info`). Replaces the pre-existing tone DRIFT where
 * `BomsTable` mapped `in_review`→`info`/`ordered`→`brand` while
 * `BomDetailPage` mapped `in_review`→`warning`/`ordered`→`info` — two
 * different tone tables for the same four statuses. This is now the single
 * source of truth both `BomCardGrid` and `BomDetailPage` import.
 */
export const BOM_STATUS_META: Record<
  BomLibraryStatus,
  { readonly tone: ChipTone; readonly icon: ReactNode; readonly description: string }
> = {
  draft: {
    tone: 'neutral',
    icon: <PauseCircleOutlined aria-hidden="true" />,
    description: 'Still being assembled — not yet ready for review',
  },
  in_review: {
    tone: 'warning',
    icon: <ClockCircleOutlined aria-hidden="true" />,
    description: 'Awaiting sign-off before it can be approved',
  },
  approved: {
    tone: 'success',
    icon: <CheckCircleOutlined aria-hidden="true" />,
    description: 'Signed off and ready to order',
  },
  ordered: {
    tone: 'info',
    icon: <TruckOutlined aria-hidden="true" />,
    description: 'Parts have been ordered',
  },
}

export interface BomStatusChipProps {
  readonly status: BomLibraryStatus
  /** Omit to render a read-only chip (no dropdown chevron/menu) — used by
   * the card grid's compact metadata row where inline status change isn't
   * offered (screens.md §5 has no per-card status menu, only the detail
   * header does). When provided, the chip becomes a
   * `aria-haspopup="menu"` button with a trailing chevron (spec's "chevron-
   * down svg 12×12 (dropdown indicator)"). */
  readonly onChange?: (next: BomLibraryStatus) => void
  readonly disabled?: boolean
  readonly className?: string
  /** UID-378 — the BOMs tab card grid's measured design spec shows the
   * status pill with NO leading status icon (soft-filled pill + trailing
   * caret only), distinct from the BOM detail header's chip/menu, which
   * keeps the icon (both in its trigger chip and its open menu's option
   * rows — this prop only ever touches the chip's own leading icon, never
   * the menu). Defaults to `false` so every existing caller (the detail
   * header) is unaffected; `BomCardGrid` is the only caller that sets it. */
  readonly hideIcon?: boolean
}

/**
 * Shared status chip (Phase 1 foundation, UID-295/296) — used by BOTH the
 * BOMs tab card grid (read-only) and the BOM detail header (interactive,
 * with the dropdown chevron for inline status change). One component, one
 * tone map, instead of the two screens rendering their own drifting chip +
 * native `<select>` pairs.
 */
export function BomStatusChip({
  status,
  onChange,
  disabled,
  className,
  hideIcon,
}: BomStatusChipProps) {
  const meta = BOM_STATUS_META[status]
  const icon = hideIcon ? undefined : meta.icon

  if (!onChange) {
    return (
      <Chip tone={meta.tone} icon={icon} className={className}>
        {BOM_STATUS_LABEL[status]}
      </Chip>
    )
  }

  return (
    <DropdownMenu
      menuLabel="Change BOM status"
      {...(className ? { className } : {})}
      renderTrigger={({ toggle, open, triggerRef }) => (
        <button
          ref={triggerRef}
          type="button"
          className="bom-status-chip-trigger"
          aria-haspopup="menu"
          aria-expanded={open}
          disabled={disabled}
          onClick={toggle}
        >
          <Chip tone={meta.tone} icon={icon}>
            {BOM_STATUS_LABEL[status]}
          </Chip>
          <DownOutlined aria-hidden="true" className="bom-status-chip-chevron" />
        </button>
      )}
      renderMenu={(close) => (
        <>
          {BOM_STATUS_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              role="menuitem"
              className="bom-status-chip-menu-item"
              aria-current={option === status || undefined}
              onClick={() => {
                onChange(option)
                close()
              }}
            >
              <span className="bom-status-chip-menu-icon" aria-hidden="true">
                {BOM_STATUS_META[option].icon}
              </span>
              <span className="bom-status-chip-menu-text">
                <span className="bom-status-chip-menu-label">{BOM_STATUS_LABEL[option]}</span>
                <span className="caption-text">{BOM_STATUS_META[option].description}</span>
              </span>
            </button>
          ))}
        </>
      )}
    />
  )
}
